
import React from 'react';
import { createRoot } from 'react-dom/client';
import App from './App';
import './index.css';

// Função aprimorada para detectar a página "Lançamento em breve" do GoDaddy
const isGoDaddyComingSoonPage = () => {
  // Verificar elementos de texto específicos do GoDaddy
  const unwantedElements = document.querySelectorAll('h1, div, p');
  for (let el of unwantedElements) {
    if (
      el.textContent?.includes('Lançamento em breve') || 
      el.textContent?.includes('Coming Soon') ||
      el.textContent?.includes('YOUR BUSINESS') ||
      el.textContent?.includes('Contact Us') ||
      el.textContent?.includes('GoDaddy') ||
      el.textContent?.includes('Website Builder')
    ) {
      console.log('Página do GoDaddy detectada, tentando redirecionar...');
      return true;
    }
  }
  
  // Verificar elementos específicos do GoDaddy
  if (document.querySelector('[class*="godaddy"]') || 
      document.querySelector('[id*="godaddy"]') || 
      document.querySelector('[src*="godaddy"]')) {
    console.log('Elementos do GoDaddy detectados na página');
    return true;
  }

  // Verificar o título da página
  if (document.title !== "Helfen FIDC — A ponte que conecta crescimento e capital") {
    document.title = "Helfen FIDC — A ponte que conecta crescimento e capital";
    console.log('Título da página incorreto, corrigindo...');
    return true;
  }
  
  // Verificar se estamos em uma página de erro do GoDaddy
  if (window.location.href.includes('error') || 
      window.location.href.includes('not-found') ||
      document.title.includes('Error') ||
      document.title.includes('Not Found')) {
    console.log('Possível página de erro detectada');
    return true;
  }
  
  return false;
};

// Função avançada para lidar com redirecionamentos conflitantes
const fixGoDaddyPage = () => {
  // Detectar domínios conhecidos (tanto s3g.capital quanto s3.capital)
  if (window.location.hostname.includes('s3g.capital') || 
      window.location.hostname.includes('s3.capital')) {
    
    // Se for a página de "Lançamento em breve" ou outra página indesejada
    if (isGoDaddyComingSoonPage()) {
      // Adicionar timestamp para evitar cache
      const timestamp = new Date().getTime();
      const random = Math.random().toString(36).substring(2, 15);
      
      // Verificar se estamos em um loop de redirecionamento
      const redirectCount = parseInt(sessionStorage.getItem('redirectCount') || '0');
      sessionStorage.setItem('redirectCount', (redirectCount + 1).toString());
      
      // Tentar várias abordagens diferentes para contornar cache e problemas de DNS
      if (redirectCount < 5) {
        console.log(`Tentativa de redirecionamento #${redirectCount + 1}`);
        
        let targetUrl;
        
        // Tentar diferentes estratégias de URL baseado no contador de redirecionamentos
        switch(redirectCount % 5) {
          case 0:
            targetUrl = `https://${window.location.hostname}/?nocache=${timestamp}&r=${random}`;
            break;
          case 1:
            targetUrl = `${window.location.protocol}//${window.location.hostname}/index.html?bypass=${timestamp}&r=${random}`;
            break;
          case 2:
            targetUrl = `${window.location.origin}/?force=${timestamp}&r=${random}`;
            break;
          case 3:
            // Tentar uma URL com hash para contornar caches agressivos
            targetUrl = `${window.location.origin}/#bypass-godaddy-${timestamp}?r=${random}`;
            break;
          case 4:
            // Usar subdomínio www se estiver no domínio raiz ou remover o www se estiver usando
            const hasWww = window.location.hostname.startsWith('www.');
            const altHost = hasWww 
              ? window.location.hostname.substring(4) 
              : `www.${window.location.hostname}`;
            targetUrl = `${window.location.protocol}//${altHost}/?bypass=${timestamp}&r=${random}`;
            break;
        }
        
        console.log(`Redirecionando para: ${targetUrl}`);
        window.location.href = targetUrl;
        return true;
      } else {
        // Se já tentamos várias vezes, tente carregar o site localmente
        console.log('Múltiplos redirecionamentos detectados, tentando renderizar localmente...');
        sessionStorage.setItem('redirectCount', '0');
        forceLocalRender();
        return false;
      }
    } else {
      // Resetar contador se a página estiver correta
      sessionStorage.setItem('redirectCount', '0');
    }
  }
  return false;
};

// Função para forçar a renderização local mesmo com problemas de DNS
const forceLocalRender = () => {
  console.log('Forçando renderização local do site...');
  
  // Limpar qualquer conteúdo GoDaddy existente
  const body = document.body;
  while (body.firstChild) {
    body.removeChild(body.firstChild);
  }
  
  // Recriar a estrutura básica do documento
  const rootDiv = document.createElement('div');
  rootDiv.id = 'root';
  document.body.appendChild(rootDiv);
  
  // Criar e aplicar estilos de reset temporários
  const tempStyle = document.createElement('style');
  tempStyle.textContent = `
    body, html { margin: 0; padding: 0; height: 100%; width: 100%; }
    * { box-sizing: border-box; }
    #root { height: 100%; width: 100%; }
  `;
  document.head.appendChild(tempStyle);
  
  // Definir título correto
  document.title = "Helfen FIDC — A ponte que conecta crescimento e capital";
  
  // Renderizar o aplicativo
  try {
    const root = createRoot(rootDiv);
    root.render(
      <React.StrictMode>
        <App />
      </React.StrictMode>
    );
    console.log('Renderização forçada concluída com sucesso');
    return true;
  } catch (error) {
    console.error('Erro durante renderização forçada:', error);
    return false;
  }
};

// Função mais robusta para limpar o cache local
const clearBrowserCache = () => {
  console.log('Tentando limpar cache do navegador...');
  
  // Limpar cache via API Cache (quando disponível)
  if ('caches' in window) {
    caches.keys().then((keyList) => {
      return Promise.all(keyList.map((key) => {
        console.log('Limpando cache:', key);
        return caches.delete(key);
      }));
    });
  }
  
  // Limpar localStorage e sessionStorage
  try {
    // Salvar o contador de redirecionamentos temporariamente
    const redirectCount = sessionStorage.getItem('redirectCount');
    
    // Limpar toda a storage
    localStorage.clear();
    sessionStorage.clear();
    
    // Restaurar o contador de redirecionamentos
    if (redirectCount) {
      sessionStorage.setItem('redirectCount', redirectCount);
    }
    
    console.log('Storage local limpo');
  } catch (e) {
    console.error('Erro ao limpar storage:', e);
  }
};

// Função mais robusta para garantir carregamento
const ensureAppLoads = () => {
  try {
    // Primeiro, tentar limpar qualquer cache
    clearBrowserCache();
    
    // Verificar se estamos na página do GoDaddy e tentar corrigir
    if (fixGoDaddyPage()) {
      console.log('Detectada página incorreta, redirecionando...');
      return; // Se estamos redirecionando, não continue com a renderização
    }
    
    // Limpar qualquer conteúdo antigo
    const rootElement = document.getElementById("root");
    if (rootElement) {
      while (rootElement.firstChild) {
        rootElement.removeChild(rootElement.firstChild);
      }
    }
    
    // Se não existir elemento root, criar um
    if (!rootElement) {
      console.log('Criando elemento root faltante...');
      const newRoot = document.createElement('div');
      newRoot.id = 'root';
      document.body.appendChild(newRoot);
    }
    
    // Renderizar o aplicativo
    const root = createRoot(document.getElementById("root")!);
    
    // Registrar evento de erro global
    window.addEventListener('error', (event) => {
      console.error('Erro global detectado:', event.error);
      if (!window.hasReloaded) {
        window.hasReloaded = true;
        console.log('Tentando recarregar após erro...');
        setTimeout(() => {
          window.location.reload();
        }, 1000);
      }
    });

    root.render(
      <React.StrictMode>
        <App />
      </React.StrictMode>
    );
    
    console.log('Helfen FIDC - Aplicação inicializada com sucesso');
  } catch (error) {
    console.error('Erro ao inicializar aplicação:', error);
    setTimeout(() => {
      // Tentar forçar renderização local antes de recarregar
      if (!forceLocalRender()) {
        window.location.reload();
      }
    }, 2000);
  }
};

// Verificar se o documento já está completamente carregado
if (document.readyState === 'complete') {
  console.log('Documento já carregado, inicializando aplicação...');
  ensureAppLoads();
} else {
  // Se não estiver carregado, esperar o evento de carregamento
  window.addEventListener('load', () => {
    console.log('Documento carregado, iniciando aplicação...');
    ensureAppLoads();
  });
}

// Verificar periodicamente se a página está correta (backup)
const checkInterval = setInterval(() => {
  if (isGoDaddyComingSoonPage()) {
    console.log('Página incorreta detectada durante verificação periódica');
    fixGoDaddyPage();
  }
}, 1000); // Verificando com mais frequência

// Limpar o intervalo após 60 segundos (quando o site já deve estar estável)
setTimeout(() => {
  clearInterval(checkInterval);
  console.log('Verificação periódica desativada, site estável');
}, 60000);

// Declaração global para TypeScript
declare global {
  interface Window {
    hasReloaded?: boolean;
  }
}
